import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClients,
  createClient,
  updateClient,
  deleteClient,
  clearClientError,
} from "../slice/clientSlice";
import { formatDateTime } from "../utils/formatters";
import ClientFilters from "../components/ClientFilters";
import Pagination from "../components/Pagination";
import ClientAutofillButton from "../components/ClientAutofillButton";
import "../css/ClientList.css";

const EMPTY_FILTERS = { name: "", industry: "", businessSegment: "", startDate: "", endDate: "" };
const EMPTY_CLIENT = { name: "", industry: "", businessSegment: "", website: "" };
const PAGE_SIZE = 10;

// Makes stored websites safe to link to: "acme.com" would otherwise be
// treated as a relative link inside the app.
const toHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// Shows "acme.com" instead of "https://www.acme.com/" in the table.
const toHost = (url) => {
  try {
    return new URL(toHref(url)).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

// Two-letter monogram for the client name column.
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// "10/2/2026, 10:53:56 AM" -> date on one line, time on the next.
const splitDateTime = (value) => {
  const text = formatDateTime(value) || "";
  const i = text.indexOf(", ");
  return i === -1 ? [text, ""] : [text.slice(0, i), text.slice(i + 2)];
};

export default function ClientList() {
  const [newClient, setNewClient] = useState(EMPTY_CLIENT);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { clients, pagination, status, error } = useSelector((state) => state.clients);
  const loading = status === "loading";
  const isAdmin = user?.role === "ADMIN";

  useEffect(() => {
    dispatch(fetchClients({ ...appliedFilters, page, limit: PAGE_SIZE }));
    return () => dispatch(clearClientError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page]);

  // Esc closes the panel (unless a save is in progress).
  useEffect(() => {
    if (!formOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) closeForm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, saving]);

  const reloadCurrentPage = () => {
    dispatch(fetchClients({ ...appliedFilters, page, limit: PAGE_SIZE }));
  };

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(draftFilters);
    dispatch(fetchClients({ ...draftFilters, page: 1, limit: PAGE_SIZE }));
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
    dispatch(fetchClients({ page: 1, limit: PAGE_SIZE }));
  };

  // Called by ClientAutofillButton with the looked-up company details.
  // Maps "clientName" -> "name" and only overwrites fields that came back non-empty.
  const handleAutofill = (d) => {
    setNewClient((p) => ({
      ...p,
      name: d.clientName || p.name,
      industry: d.industry || p.industry,
      businessSegment: d.businessSegment || p.businessSegment,
      website: d.website || p.website,
    }));
  };

  const openAdd = () => {
    setEditingId(null);
    setFormError("");
    setNewClient(EMPTY_CLIENT);
    setFormOpen(true);
  };

  const startEdit = (c) => {
    setEditingId(c._id);
    setFormError("");
    setNewClient({
      name: c.name || "",
      industry: c.industry || "",
      businessSegment: c.businessSegment || "",
      website: c.website || "",
    });
    setFormOpen(true);
  };

  // Same as the old cancelEdit, plus closing the panel.
  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormError("");
    setNewClient(EMPTY_CLIENT);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!newClient.name.trim()) {
      setFormError("Client name is required.");
      return;
    }

    setSaving(true);
    const result = editingId
      ? await dispatch(updateClient({ id: editingId, data: newClient }))
      : await dispatch(createClient(newClient));
    setSaving(false);

    const succeeded = editingId
      ? updateClient.fulfilled.match(result)
      : createClient.fulfilled.match(result);

    if (succeeded) {
      closeForm();
      reloadCurrentPage();
    } else {
      // Show the error (e.g. duplicate name) inside the form, not in the top banner
      setFormError(result.payload || "Something went wrong. Please try again.");
      dispatch(clearClientError());
    }
  };

  const handleDelete = async (c) => {
    const ok = window.confirm(
      `Delete client "${c.name}"?\n\nThis cannot be undone. Optimizations already created for this client may lose their client link.`
    );
    if (!ok) return;

    const result = await dispatch(deleteClient(c._id));
    if (deleteClient.fulfilled.match(result)) {
      if (editingId === c._id) closeForm();
      // If we just deleted the last row on this page, step back one page
      if (clients.length === 1 && page > 1) setPage(page - 1);
      else reloadCurrentPage();
    }
    // on failure, the slice sets `error`, which shows in the top banner
  };

  const setField = (key) => (e) => setNewClient((p) => ({ ...p, [key]: e.target.value }));

  const renderTable = () => {
    if (loading && clients.length === 0) {
      return <div className="cl-empty">Loading clients…</div>;
    }
    if (clients.length === 0) {
      return (
        <div className="cl-empty">
          <strong>No clients match these filters.</strong>
          <span>{isAdmin ? "Try clearing the filters above." : "Clear the filters, or add a new client."}</span>
        </div>
      );
    }

    return (
      <>
        <div className="cl-table-wrap">
          <table className={`cl-table ${isAdmin ? "cl-table-admin" : "cl-table-user"}`}>
            <colgroup>
              <col className="cl-col-client" />
              <col className="cl-col-industry" />
              <col className="cl-col-website" />
              {isAdmin && <col className="cl-col-by" />}
              <col className="cl-col-date" />
              {!isAdmin && <col className="cl-col-actions" />}
            </colgroup>
            <thead>
              <tr>
                <th>Client</th>
                <th>Industry / segment</th>
                <th>Website</th>
                {isAdmin && <th>Added by</th>}
                <th>Added on</th>
                {!isAdmin && <th className="cl-th-actions">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => {
                const [date, time] = splitDateTime(c.createdAt);
                return (
                  <tr key={c._id} className={editingId === c._id ? "is-editing" : undefined}>
                    <td>
                      <div className="cl-name">
                        <span className="cl-mono">{initials(c.name)}</span>
                        <span className="cl-name-text">{c.name}</span>
                      </div>
                    </td>
                    <td>
                      {c.industry || c.businessSegment ? (
                        <div className="cl-industry">
                          {c.industry && <span className="cl-chip">{c.industry}</span>}
                          {c.businessSegment && <span className="cl-segment">{c.businessSegment}</span>}
                        </div>
                      ) : (
                        <span className="cl-dash">—</span>
                      )}
                    </td>
                    <td>
                      {c.website ? (
                        <a className="cl-link" href={toHref(c.website)} target="_blank" rel="noreferrer" title={c.website}>
                          {toHost(c.website)}
                        </a>
                      ) : (
                        <span className="cl-dash">—</span>
                      )}
                    </td>
                    {isAdmin && <td className="cl-wrap">{c.createdBy?.name || <span className="cl-dash">—</span>}</td>}
                    <td>
                      <div className="cl-date">
                        <span>{date}</span>
                        {time && <span className="cl-time">{time}</span>}
                      </div>
                    </td>
                    {!isAdmin && (
                      <td className="cl-actions">
                        <button type="button" className="cl-btn cl-btn-row" onClick={() => startEdit(c)}>
                          Edit
                        </button>
                        <button type="button" className="cl-btn cl-btn-row cl-btn-danger" onClick={() => handleDelete(c)}>
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="cl-pagination">
          <Pagination pagination={pagination} onPageChange={setPage} />
        </div>
      </>
    );
  };

  return (
    <div className="cl-page">
      <header className="cl-header">
        <div>
          <h2>Clients</h2>
          <p>{isAdmin ? "Every client added across your teams." : "Companies you submit candidates to."}</p>
        </div>
        {!isAdmin && (
          <button type="button" className="cl-btn cl-btn-add" onClick={openAdd}>
            <span aria-hidden="true">+</span> Add client
          </button>
        )}
      </header>

      {error && (
        <div className="cl-error" role="alert">
          {error}
        </div>
      )}

      {/* ClientFilters draws its own card, so it isn't wrapped in another one. */}
      <div className="cl-filters">
        <ClientFilters value={draftFilters} onChange={setDraftFilters} onApply={applyFilters} onReset={resetFilters} />
      </div>

      <section className="cl-card">
        <div className="cl-card-head">
          <h2>{isAdmin ? "All clients" : "Existing clients"}</h2>
        </div>
        {renderTable()}
      </section>

      {!isAdmin && formOpen && (
        <div className="cl-drawer-root">
          <div className="cl-backdrop" onClick={() => !saving && closeForm()} />
          <form
            className="cl-drawer"
            onSubmit={handleSubmit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cl-drawer-title"
          >
            <div className="cl-drawer-head">
              <div>
                <h2 id="cl-drawer-title">{editingId ? "Edit client" : "Add client"}</h2>
                <p>
                  {editingId
                    ? "Changes apply to this client everywhere it's used."
                    : "Enter a company name, then autofill the rest."}
                </p>
              </div>
              <button
                type="button"
                className="cl-btn cl-btn-close"
                onClick={closeForm}
                disabled={saving}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="cl-drawer-body">
              <div className="cl-field">
                <label htmlFor="cl-name">
                  Client name <span className="cl-req">*</span>
                </label>
                <input
                  id="cl-name"
                  type="text"
                  value={newClient.name}
                  onChange={setField("name")}
                  placeholder="e.g. Northwind Financial"
                  autoFocus
                  required
                />
              </div>

              <div className="cl-autofill">
                <ClientAutofillButton clientName={newClient.name} onFill={handleAutofill} />
              </div>

              <div className="cl-field">
                <label htmlFor="cl-industry">Industry</label>
                <input
                  id="cl-industry"
                  type="text"
                  value={newClient.industry}
                  onChange={setField("industry")}
                  placeholder="e.g. Healthcare, E-commerce"
                />
              </div>

              <div className="cl-field">
                <label htmlFor="cl-segment">Business segment</label>
                <input
                  id="cl-segment"
                  type="text"
                  value={newClient.businessSegment}
                  onChange={setField("businessSegment")}
                  placeholder="e.g. Retail banking"
                />
              </div>

              <div className="cl-field">
                <label htmlFor="cl-website">Website</label>
                <input
                  id="cl-website"
                  type="text"
                  value={newClient.website}
                  onChange={setField("website")}
                  placeholder="https://www.company.com"
                />
              </div>

              {formError && (
                <div className="cl-form-error" role="alert">
                  {formError}
                </div>
              )}
            </div>

            <div className="cl-drawer-foot">
              <button type="button" className="cl-btn cl-btn-secondary" onClick={closeForm} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="cl-btn cl-btn-primary" disabled={saving}>
                {saving ? "Saving…" : editingId ? "Update client" : "Save client"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
